export type ToolbarFormatCommand = 'bold' | 'italic' | 'insertOrderedList';

export function syncToolbarState(
  editor: HTMLElement,
  buttons: HTMLButtonElement[],
) {
  const active = selectionInsideEditor(editor);
  for (const button of buttons) {
    const command = button.dataset.command as ToolbarFormatCommand | undefined;
    const isActive = active && !!command && isFormatCommandActive(editor, command);
    button.classList.toggle('is-active', isActive);
  }
}

export function selectionInsideEditor(editor: HTMLElement) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const anchorNode = selection.anchorNode;
  return !!anchorNode && editor.contains(anchorNode);
}

function isFormatCommandActive(editor: HTMLElement, command: ToolbarFormatCommand) {
  if (formatAppliesAtSelection(editor, command)) return true;

  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

function formatAppliesAtSelection(editor: HTMLElement, command: ToolbarFormatCommand) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const points = [
    { node: selection.anchorNode, offset: selection.anchorOffset },
    { node: selection.focusNode, offset: selection.focusOffset },
  ];

  for (const point of points) {
    if (!point.node || !editor.contains(point.node)) continue;
    if (nodeHasFormat(editor, point.node, point.offset, command)) return true;
  }

  return false;
}

function nodeHasFormat(
  editor: HTMLElement,
  node: Node,
  offset: number,
  command: ToolbarFormatCommand,
) {
  for (const candidate of getNodesToCheck(node, offset)) {
    if (walkAncestorsForFormat(candidate, editor, command)) return true;
  }

  return false;
}

function getNodesToCheck(node: Node, offset: number) {
  if (node.nodeType === Node.TEXT_NODE) return [node];

  if (node.nodeType !== Node.ELEMENT_NODE) return [node];

  const element = node as Element;
  const child = element.childNodes[offset] ?? element.childNodes[offset - 1];
  return child ? [child, element] : [element];
}

function walkAncestorsForFormat(
  node: Node | null,
  stopAt: HTMLElement,
  command: ToolbarFormatCommand,
) {
  let current: Node | null = node;
  while (current && current !== stopAt) {
    if (current instanceof HTMLElement && elementMatchesFormat(current, command)) {
      return true;
    }
    current = current.parentNode;
  }

  return false;
}

function elementMatchesFormat(element: HTMLElement, command: ToolbarFormatCommand) {
  switch (command) {
    case 'bold':
      return isBoldElement(element);
    case 'italic':
      return isItalicElement(element);
    case 'insertOrderedList':
      return element.tagName === 'OL'
        || (element.tagName === 'LI' && element.closest('ol') !== null);
    default:
      return false;
  }
}

function isBoldElement(element: HTMLElement) {
  const tag = element.tagName;
  if (tag === 'B' || tag === 'STRONG') return true;

  const weight = element.style.fontWeight;
  return weight === 'bold' || weight === '700';
}

function isItalicElement(element: HTMLElement) {
  const tag = element.tagName;
  if (tag === 'I' || tag === 'EM') return true;

  return element.style.fontStyle === 'italic';
}
